import {
  AntivirusScanStatusEnum,
  antivirusScanStatusFromName,
} from '../../enums/AntivirusScanStatusEnum';
import type File from '../../interfaces/File';

/** Product default extension list when ANTIVIRUS_SCAN was never configured (15 entries). */
export const DEFAULT_ANTIVIRUS_EXTENSIONS: readonly string[] = [
  'exe',
  'dll',
  'msi',
  'zip',
  'rar',
  '7z',
  'docx',
  'docm',
  'xlsx',
  'xlsm',
  'pptx',
  'pdf',
  'js',
  'vbs',
  'ps1',
] as const;

/** Known scan-failure codes (BE / fixture). Normalized uppercase. */
export const AntivirusScanErrorCode = {
  StreamTooLarge: 'STREAM_TOO_LARGE',
  EngineUnreachable: 'ENGINE_UNREACHABLE',
  ScanTimeout: 'SCAN_TIMEOUT',
  MaxRetries: 'MAX_RETRIES',
} as const;

export type AntivirusScanErrorCodeValue =
  (typeof AntivirusScanErrorCode)[keyof typeof AntivirusScanErrorCode];

/** Codes where retry cannot succeed (size/stream limits). Kept for Error+code transition. */
const NON_RETRYABLE_ANTIVIRUS_SCAN_ERRORS = new Set<string>([
  AntivirusScanErrorCode.StreamTooLarge,
]);

const KNOWN_ANTIVIRUS_SCAN_ERROR_I18N = new Set<string>(Object.values(AntivirusScanErrorCode));

export type AntivirusUiKind =
  'none' | 'pending' | 'rescanPending' | 'infected' | 'error' | 'rescanFailed' | 'tooLarge';

export interface AntivirusUiState {
  kind: AntivirusUiKind;
  /** Actions that hand out content must be disabled. */
  blocked: boolean;
  /** Owner Retry scan (Error + empty threat + retryable code). Never for TooLarge. */
  showOwnerRetry: boolean;
  /** Threat / signature name when present. */
  threatName: string;
  /** Normalized scan-error code when Error (may be empty if BE omitted it). */
  errorCode: string;
}

/** BE wire fields on File (`avScanStatus` / `avThreatName` / `avScanError`). */
type AntivirusFileFields =
  Pick<File, 'avScanStatus' | 'avThreatName' | 'avScanError'> | null | undefined;

/** Trim + uppercase + hyphen→underscore. Empty when missing. */
export function normalizeAntivirusScanError(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return trimmed
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/-/g, '_')
    .toUpperCase();
}

export function isAntivirusScanErrorRetryable(errorCode: string): boolean {
  if (!errorCode) return true;
  return !NON_RETRYABLE_ANTIVIRUS_SCAN_ERRORS.has(errorCode);
}

/** Non-empty avThreatName (BE wire) ⇒ blocked, regardless of avScanStatus. */
export function isAntivirusBlocked(file: AntivirusFileFields): boolean {
  if (!file) return false;
  return (file.avThreatName ?? '').trim().length > 0;
}

/**
 * Status → UI decision table (spec).
 * When antivirus licence is off, caller should not render antivirus UI at all.
 *
 * TooLarge (status 6) and legacy Error + STREAM_TOO_LARGE → distinct tooLarge UX:
 * no Retry, actions remain enabled.
 */
export function getAntivirusUiState(file: AntivirusFileFields): AntivirusUiState {
  const status = file?.avScanStatus ?? AntivirusScanStatusEnum.None;
  const threatName = (file?.avThreatName ?? '').trim();
  const hasThreat = threatName.length > 0;
  const blocked = hasThreat;
  const errorCode =
    status === AntivirusScanStatusEnum.Error ? normalizeAntivirusScanError(file?.avScanError) : '';

  if (status === AntivirusScanStatusEnum.Pending && !hasThreat) {
    return {
      kind: 'pending',
      blocked: false,
      showOwnerRetry: false,
      threatName: '',
      errorCode: '',
    };
  }
  if (status === AntivirusScanStatusEnum.Pending && hasThreat) {
    return {
      kind: 'rescanPending',
      blocked: true,
      showOwnerRetry: false,
      threatName,
      errorCode: '',
    };
  }
  if (status === AntivirusScanStatusEnum.Infected) {
    return { kind: 'infected', blocked: true, showOwnerRetry: false, threatName, errorCode: '' };
  }

  // Status 6, or transitional Error + STREAM_TOO_LARGE
  if (
    status === AntivirusScanStatusEnum.TooLarge ||
    (status === AntivirusScanStatusEnum.Error &&
      !hasThreat &&
      errorCode === AntivirusScanErrorCode.StreamTooLarge)
  ) {
    return {
      kind: 'tooLarge',
      blocked: false,
      showOwnerRetry: false,
      threatName: '',
      errorCode:
        errorCode ||
        (status === AntivirusScanStatusEnum.TooLarge ? AntivirusScanErrorCode.StreamTooLarge : ''),
    };
  }

  if (status === AntivirusScanStatusEnum.Error && !hasThreat) {
    return {
      kind: 'error',
      blocked: false,
      showOwnerRetry: isAntivirusScanErrorRetryable(errorCode),
      threatName: '',
      errorCode,
    };
  }
  if (status === AntivirusScanStatusEnum.Error && hasThreat) {
    return {
      kind: 'rescanFailed',
      blocked: true,
      showOwnerRetry: false,
      threatName,
      errorCode,
    };
  }

  // None / Clean / Skipped (and unexpected combo without threat)
  return { kind: 'none', blocked, showOwnerRetry: false, threatName, errorCode: '' };
}

type TranslateFn = (key: string, opts?: Record<string, string>) => string;

/** i18n key for Error-state copy; specific when BE sends a known code. */
export function getAntivirusErrorMessageKey(errorCode: string): string {
  if (errorCode && KNOWN_ANTIVIRUS_SCAN_ERROR_I18N.has(errorCode)) {
    return `file.antivirus.errors.${errorCode}`;
  }
  return 'file.antivirus.error';
}

export function getAntivirusErrorMessage(t: TranslateFn, errorCode: string): string {
  return t(getAntivirusErrorMessageKey(errorCode));
}

/** Trim, strip leading dot, lowercase, dedupe — order preserved. */
export function normalizeAntivirusExtensions(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const normalized = item.trim().replace(/^\.+/, '').toLowerCase();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
}

/** Client-side save gate: enabled + empty extensions is refused. */
export function isAntivirusScanSettingsValid(enabled: boolean, extensions: string[]): boolean {
  if (!enabled) return true;
  return normalizeAntivirusExtensions(extensions).length > 0;
}

export interface AntivirusEventPatch {
  fileId: number;
  /** BE wire field name on File. */
  avScanStatus: AntivirusScanStatusEnum;
  /**
   * When undefined, merge must preserve the row's existing threat
   * (Error/Pending events omit `av_threat_name` but quarantine may still hold).
   * BE wire field name on File.
   */
  avThreatName?: string;
  /**
   * When undefined, merge preserves existing error code.
   * Cleared (empty string) on Clean / Infected / Skipped / None / Pending / TooLarge.
   * BE wire field name on File.
   */
  avScanError?: string;
}

/**
 * Build a file-row patch from `antivirus_event` / `av_scan` payload.
 * Spec: `av_threat_name` only on Infected. Clean/Skipped/TooLarge clear threat.
 * Error: omit threat so merge keeps quarantine marker if any.
 * Optional `av_scan_error` / `avScanError` when Error (not in original 1337 contract).
 */
export function patchFromAntivirusEvent(data: Record<string, unknown>): AntivirusEventPatch | null {
  const fileIdRaw = data.file_id ?? data.fileId;
  const fileId =
    typeof fileIdRaw === 'number'
      ? fileIdRaw
      : typeof fileIdRaw === 'string'
        ? Number.parseInt(fileIdRaw, 10)
        : NaN;
  if (!Number.isFinite(fileId)) return null;

  const statusName =
    typeof data.av_scan_status === 'string'
      ? data.av_scan_status
      : typeof data.avScanStatus === 'string'
        ? data.avScanStatus
        : undefined;

  const status = antivirusScanStatusFromName(statusName);
  if (status == null) return null;

  const threatRaw = data.av_threat_name ?? data.avThreatName;
  const threatFromPayload = typeof threatRaw === 'string' ? threatRaw.trim() : undefined;

  const errorRaw = data.av_scan_error ?? data.avScanError;
  const errorFromPayload =
    typeof errorRaw === 'string' ? normalizeAntivirusScanError(errorRaw) : undefined;

  if (status === AntivirusScanStatusEnum.Infected) {
    return {
      fileId,
      avScanStatus: status,
      avThreatName: threatFromPayload ?? '',
      avScanError: '',
    };
  }

  if (
    status === AntivirusScanStatusEnum.Clean ||
    status === AntivirusScanStatusEnum.Skipped ||
    status === AntivirusScanStatusEnum.None ||
    status === AntivirusScanStatusEnum.TooLarge
  ) {
    return { fileId, avScanStatus: status, avThreatName: '', avScanError: '' };
  }

  // Pending / Error: preserve existing threat unless event explicitly sends one.
  // Pending clears scan-error (new attempt). Error may carry av_scan_error.
  if (status === AntivirusScanStatusEnum.Pending) {
    return {
      fileId,
      avScanStatus: status,
      avScanError: '',
      ...(threatFromPayload !== undefined ? { avThreatName: threatFromPayload } : {}),
    };
  }

  return {
    fileId,
    avScanStatus: status,
    ...(threatFromPayload !== undefined ? { avThreatName: threatFromPayload } : {}),
    ...(errorFromPayload !== undefined ? { avScanError: errorFromPayload } : {}),
  };
}

/** User-facing infected message from API payload or list row context. */
export function getAntivirusInfectedMessage(
  t: TranslateFn,
  opts?: { threatName?: string | null; fileName?: string | null },
): string {
  const threatName = (opts?.threatName ?? '').trim();
  const fileName = (opts?.fileName ?? '').trim();
  if (threatName.length > 0) {
    return t('errors.antivirusFileInfectedNamed', {
      threatName,
      ...(fileName.length > 0 ? { fileName } : {}),
    });
  }
  return t('errors.antivirusFileInfected');
}
